import React, { createContext, useContext, useMemo } from "react";
import { RaffleParticipant } from "@/apps/raffle/types";
import {
  buildSectors,
  CENTER,
  HORIZONTAL_SAFE_GAP,
  RIM_WIDTH,
  WHITE_RIM_WIDTH,
  WHEEL_SIZE,
} from "./wheelMath";

type RaffleWheelContextValue = {
  currentParticipantIndex?: number;
  scale: number;
  baseViewportScale: number;
  center: number;
  radius: number;
  wheelSize: number;
  normalWheelSize: number;
  sectors: ReturnType<typeof buildSectors>;
};

const RaffleWheelContext = createContext<RaffleWheelContextValue | null>(null);

type ProviderProps = {
  participants: RaffleParticipant[];
  currentParticipantIndex?: number;
  children: React.ReactNode;
};

export const RaffleWheelProvider = ({
  participants,
  currentParticipantIndex,
  children,
}: ProviderProps) => {
  const scale = 1;
  const baseViewportScale =
    typeof window === "undefined"
      ? 1
      : Math.min(1, (window.innerWidth - HORIZONTAL_SAFE_GAP) / WHEEL_SIZE);
  const layoutScale = scale * baseViewportScale;
  const center = CENTER * scale;
  const radius = center - RIM_WIDTH - WHITE_RIM_WIDTH;
  const wheelSize = WHEEL_SIZE * layoutScale;
  const normalWheelSize = WHEEL_SIZE * baseViewportScale;
  const sectors = useMemo(
    () => buildSectors(participants),
    [participants]
  );

  const value = useMemo<RaffleWheelContextValue>(
    () => ({
      currentParticipantIndex,
      scale,
      baseViewportScale,
      center,
      radius,
      wheelSize,
      normalWheelSize,
      sectors,
    }),
    [
      baseViewportScale,
      center,
      currentParticipantIndex,
      normalWheelSize,
      radius,
      scale,
      sectors,
      wheelSize,
    ]
  );

  return (
    <RaffleWheelContext.Provider value={value}>
      {children}
    </RaffleWheelContext.Provider>
  );
};

export const useRaffleWheel = () => {
  const context = useContext(RaffleWheelContext);

  if (!context) {
    throw new Error("useRaffleWheel must be used within RaffleWheelProvider");
  }

  return context;
};
